import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';

type PermissionState = 'unsupported' | 'default' | 'granted' | 'denied';

interface VapidConfig {
  publicKey: string | null;
  configured: boolean;
}

interface UsePushResult {
  permission: PermissionState;
  subscribed: boolean;
  loading: boolean;
  vapidConfigured: boolean;
  /** Request OS permission + subscribe the current device to push. */
  enable: () => Promise<void>;
  /** Unsubscribe the current device. */
  disable: () => Promise<void>;
  /** Ask the backend to send a test notification to this user. */
  sendTest: () => Promise<void>;
  error: string | null;
}

export function usePush(): UsePushResult {
  const [permission, setPermission] = useState<PermissionState>(() => detectPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vapidConfigured, setVapidConfigured] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshSubscribed = useCallback(async () => {
    if (permission !== 'granted') {
      setSubscribed(false);
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch {
      setSubscribed(false);
    }
  }, [permission]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (permission === 'unsupported') return;
      // Fetch VAPID config once — the Enable button needs it.
      try {
        const { configured } = await api.get<VapidConfig>('/api/push/vapid-public-key');
        if (!cancelled) setVapidConfigured(configured);
      } catch {
        if (!cancelled) setVapidConfigured(false);
      }
      await refreshSubscribed();
    })();
    return () => { cancelled = true; };
  }, [permission, refreshSubscribed]);

  const enable = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      if (permission === 'unsupported') throw new Error('This browser doesn\'t support push notifications.');

      const result = await Notification.requestPermission();
      setPermission(result as PermissionState);
      if (result !== 'granted') throw new Error('Notifications were blocked. Enable them in your browser/app settings and try again.');

      const { publicKey, configured } = await api.get<VapidConfig>('/api/push/vapid-public-key');
      if (!configured || !publicKey) throw new Error('The server isn\'t configured for push yet. Ask an admin.');

      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      const sub = existing ?? await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      const json = sub.toJSON();
      await api.post('/api/push/subscribe', {
        endpoint: json.endpoint,
        keys: {
          p256dh: json.keys?.p256dh,
          auth: json.keys?.auth,
        },
        userAgent: navigator.userAgent,
      });
      setSubscribed(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [permission]);

  const disable = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await api.post('/api/push/unsubscribe', { endpoint }).catch(() => {});
      }
      setSubscribed(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    setError(null);
    try {
      await api.post('/api/push/test');
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  return { permission, subscribed, loading, vapidConfigured, enable, disable, sendTest, error };
}

function detectPermission(): PermissionState {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('Notification' in window)) return 'unsupported';
  if (!('serviceWorker' in navigator)) return 'unsupported';
  if (!('PushManager' in window)) return 'unsupported';
  return Notification.permission as PermissionState;
}

// Standard VAPID key decoder — web-push library documents this shape.
// Backing buffer must be a plain ArrayBuffer (not SharedArrayBuffer) to satisfy
// PushManager.subscribe's BufferSource<ArrayBuffer> type.
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const buf = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
